/**
 * Type Result pour la gestion fonctionnelle des erreurs
 * Inspiré du pattern Railway-Oriented Programming
 *
 * Permet d'éviter les exceptions et de gérer les erreurs de manière explicite
 */

export interface Success<T> {
  readonly success: true;
  readonly value: T;
}

export interface Failure<E> {
  readonly success: false;
  readonly error: E;
}

export type Result<T, E = Error> = Success<T> | Failure<E>;

export namespace Result {
  /**
   * Crée un Result représentant un succès
   */
  export function ok<T, E = never>(value: T): Result<T, E> {
    return { success: true, value } as Result<T, E>;
  }

  /**
   * Crée un Result représentant un échec
   */
  export function fail<T = never, E = Error>(error: E): Result<T, E> {
    return { success: false, error } as Result<T, E>;
  }

  /**
   * Combine plusieurs Results
   * Si tous sont ok, retourne ok avec un tableau des valeurs
   * Sinon retourne le premier échec rencontré
   */
  export function combine<T, E>(results: Result<T, E>[]): Result<T[], E> {
    const values: T[] = [];

    for (const result of results) {
      if (!result.success) {
        return Result.fail(result.error);
      }
      values.push(result.value);
    }

    return Result.ok(values);
  }

  /**
   * Transforme un Result en Promise
   */
  export function toPromise<T, E>(result: Result<T, E>): Promise<T> {
    if (result.success) {
      return Promise.resolve(result.value);
    }
    return Promise.reject(result.error);
  }

  /**
   * Crée un Result depuis une Promise
   */
  export async function fromPromise<T, E = Error>(
    promise: Promise<T>,
    errorMapper?: (error: unknown) => E
  ): Promise<Result<T, E>> {
    try {
      const value = await promise;
      return Result.ok(value);
    } catch (error) {
      const mappedError = errorMapper ? errorMapper(error) : (error as E);
      return Result.fail(mappedError);
    }
  }
}

/**
 * Type guard pour vérifier si un Result est un succès
 */
export function isSuccess<T, E>(result: Result<T, E>): result is Success<T> {
  return result.success;
}

/**
 * Type guard pour vérifier si un Result est un échec
 */
export function isFailure<T, E>(result: Result<T, E>): result is Failure<E> {
  return !result.success;
}
